/**
 * EntryLanguageRedirect
 * Al ENTRAR en la web por una URL sin prefijo (español) redirige al idioma que
 * el visitante eligió la última vez, si es soportado y distinto del español.
 *
 * Reglas: solo se evalúa en el primer render (la navegación interna posterior
 * nunca redirige); una entrada con prefijo (`/fr/...`) se respeta tal cual y no
 * toca la preferencia (solo el selector la escribe); sin preferencia válida el
 * comportamiento no cambia (visitantes nuevos y buscadores siguen en español).
 *
 * Decisión de diseño: para evitar un parpadeo con contenido en español, la
 * decisión se toma de forma síncrona en el primer render (estado inicial) y
 * mientras hay una redirección pendiente no se renderizan los hijos (solo
 * un instante en blanco, sin contenido en español). Se navega en `useEffect`
 * y no en `useLayoutEffect` porque el Router engancha su listener de historial
 * en un layout effect propio, que corre DESPUÉS del de sus hijos y perdería
 * la navegación. Envuelve solo las rutas, dentro
 * del Router, y `LanguageSync` va fuera para seguir a la URL.
 */

import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';
import { DEFAULT_LANGUAGE } from './languages';
import { getLanguageFromPath, switchLanguagePath } from './paths';
import { getSavedLanguage } from './languagePreference';

/**
 * Calcula la URL de destino de la redirección de entrada, o `null` si no aplica.
 * @param {{pathname: string, search: string, hash: string}} location - URL de entrada.
 * @returns {string|null}
 */
const getEntryRedirect = ({ pathname, search, hash }) => {
  if (getLanguageFromPath(pathname) !== DEFAULT_LANGUAGE) return null;
  const saved = getSavedLanguage();
  if (!saved || saved === DEFAULT_LANGUAGE) return null;
  return `${switchLanguagePath(pathname, saved)}${search}${hash}`;
};

/**
 * @param {Object} props
 * @param {React.ReactNode} props.children - Contenido (rutas) a mostrar cuando no hay redirección pendiente.
 * @returns {JSX.Element|null}
 */
const EntryLanguageRedirect = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  // Se calcula una sola vez: solo cuenta la URL de entrada.
  const [target] = useState(() => getEntryRedirect(location));
  const [done, setDone] = useState(target === null);

  // La redirección se ejecuta una única vez. En React Router v6 `navigate`
  // cambia de identidad en cada cambio de ruta, así que sin esta guarda el
  // efecto volvería a lanzarse tras cada navegación (p. ej. al elegir otro
  // idioma en el selector) y devolvería al usuario a la URL de entrada.
  const redirected = useRef(false);

  useEffect(() => {
    if (redirected.current) return;
    redirected.current = true;
    if (target) navigate(target, { replace: true });
    setDone(true);
  }, [target, navigate]);

  return done ? <>{children}</> : null;
};

EntryLanguageRedirect.propTypes = {
  children: PropTypes.node,
};

export default EntryLanguageRedirect;
